import clsx from 'clsx'
import type { ReactNode } from 'react'
import { Redact } from '@/common/analytics'
import { Icon } from '@/common/components/icon'
import { Text } from '@/common/components/text'
import { Tooltip } from '@/common/components/tooltip'
import * as styles from './PropertyPricingGrid.css'

/** The prices this grid reads; a price the viewer was not given is null. */
export interface PropertyPricingFields {
  market_value?: number | null
  floor_price?: number | null
  house_canary_valuation?: number | null
}

/** Whether the listing agent shares each price with investors. */
export interface PropertyPriceSharing {
  market_value: boolean
  floor_price: boolean
}

interface PropertyPricingGridProps {
  property: PropertyPricingFields
  /**
   * The listing agent's view: each price label shows whether it is shared, and
   * the AVM estimate is left out.
   */
  sharing?: PropertyPriceSharing
  /** Leave out a price the viewer was not given instead of showing a dash. */
  hideMissingPrices?: boolean
  className?: string
}

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—'
  return currencyFormat.format(value)
}

function SharingIcon({ shared, sharedText, unsharedText }: { shared: boolean; sharedText: string; unsharedText: string }) {
  return (
    <Tooltip content={shared ? sharedText : unsharedText} delayDuration={0}>
      <span className={styles.labelIcon}>
        <Icon name={shared ? 'visibility' : 'visibility_off'} size={14} />
      </span>
    </Tooltip>
  )
}

function PricingItem({ label, value, labelIcon }: { label: string; value: number | null | undefined; labelIcon?: ReactNode }) {
  return (
    <div className={styles.pricingItem}>
      <Text as="span" variant="label" color="muted" className={styles.pricingLabel}>
        {label}
        {labelIcon}
      </Text>
      <Text as="p" variant="h4" className={styles.pricingValue}>
        <Redact>{formatCurrency(value)}</Redact>
      </Text>
    </div>
  )
}

/**
 * Recommended list price, customer floor price and AVM estimate, side by side.
 * Renders nothing when there is no price to show.
 */
export function PropertyPricingGrid({ property, sharing, hideMissingPrices, className }: PropertyPricingGridProps) {
  const { market_value, floor_price, house_canary_valuation } = property
  const showMarketValue = !hideMissingPrices || market_value != null
  const showFloorPrice = !hideMissingPrices || floor_price != null
  const showAvm = !sharing && house_canary_valuation != null

  if (!showMarketValue && !showFloorPrice && !showAvm) {
    return null
  }

  return (
    <div className={clsx(styles.pricingGrid, className)}>
      {showMarketValue && (
        <PricingItem
          label="RECOMMENDED LIST PRICE"
          value={market_value}
          labelIcon={
            sharing && (
              <SharingIcon
                shared={sharing.market_value}
                sharedText="Recommended list price shared with investors"
                unsharedText="Recommended list price not shared with investors"
              />
            )
          }
        />
      )}
      {showFloorPrice && (
        <PricingItem
          label="CUSTOMER FLOOR PRICE"
          value={floor_price}
          labelIcon={
            sharing && (
              <SharingIcon
                shared={sharing.floor_price}
                sharedText="Shared with investors"
                unsharedText="Not shared with investors"
              />
            )
          }
        />
      )}
      {showAvm && (
        <PricingItem
          label="AVM ESTIMATE"
          value={house_canary_valuation}
          labelIcon={
            <Tooltip
              content="Automated Valuation Model estimate from House Canary, an independent third-party provider"
              delayDuration={0}
            >
              <span className={styles.labelIcon}>
                <Icon name="info" size={14} />
              </span>
            </Tooltip>
          }
        />
      )}
    </div>
  )
}
